import { useCallback, useMemo } from 'react';

import { timeframes } from '../sidebar/ModalAddContent/constants';
import { FilterType, PostedByType } from '../sidebar/ModalAddContent/types';
import { useAddContentStore } from '../sidebar/useAddContentStore';

export const usePostedBy = () => {
  const {
    postedBy,
    filters,
    setPostedBy,
    resetPostedBy,
    removeFilter,
    setFilters,
  } = useAddContentStore();

  const calculateDate = (daysAgo: number) => {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return date.toISOString();
  };

  const convertToISOString = (dateStr: string) =>
    new Date(dateStr).toISOString();

  const selectedTime = useMemo(() => {
    if (postedBy === 0) {
      return { friendlyValue: 'Anytime', value: 0 };
    }
    if (postedBy === 'custom') {
      // Retrieve the custom date filters.
      const postedAfterFilter = filters.find(
        (filter) =>
          filter.name === 'published_at' && filter.comparator === 'gte',
      );
      const postedBeforeFilter = filters.find(
        (filter) =>
          filter.name === 'published_at' && filter.comparator === 'lte',
      );

      // Set a default friendly value.
      let friendlyValue = 'Custom';

      // Format the date range if both filters exist.
      if (postedAfterFilter && postedBeforeFilter) {
        const options: Intl.DateTimeFormatOptions = {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        };
        const postedAfterDate = new Date(postedAfterFilter.value as string);
        const postedBeforeDate = new Date(postedBeforeFilter.value as string);
        friendlyValue = `${postedAfterDate.toLocaleDateString(
          'en-US',
          options,
        )} - ${postedBeforeDate.toLocaleDateString('en-US', options)}`;
      }

      return { friendlyValue, value: 'custom' };
    }
    // For other timeframes, return the matching item from the timeframes array.
    const item = timeframes.find((t) => t.value === postedBy);
    return item;
  }, [postedBy, filters]);

  const handleSelectPostedBy = useCallback(
    (value: PostedByType) => {
      if (value === 0) {
        resetPostedBy();
        removeFilter('published_at');
        return;
      }
      const calculatedDate = calculateDate(value as number);
      const postedByFilter: FilterType = {
        comparator: 'gte',
        name: 'published_at',
        value: calculatedDate,
      };
      setPostedBy(value);
      setFilters(postedByFilter);
    },
    [setPostedBy, setFilters, resetPostedBy, removeFilter],
  );

  const handleSelectCustomDates = useCallback(
    (dates: { postedAfter?: string | null; postedBefore?: string | null }) => {
      if (!dates.postedAfter || !dates.postedBefore) return;
      // Remove previous custom date filters for published_at
      removeFilter('published_at');

      const postedAfterISO = convertToISOString(dates.postedAfter);
      const postedBeforeISO = convertToISOString(dates.postedBefore);

      // Set a filter for dates after the specified start date.
      const postedAfterFilter: FilterType = {
        comparator: 'gte',
        name: 'published_at',
        value: postedAfterISO,
      };

      const postedBeforeFilter: FilterType = {
        comparator: 'lte',
        name: 'published_at',
        value: postedBeforeISO,
      };

      // Add both filters.
      setPostedBy('custom');
      setFilters(postedAfterFilter);
      setFilters(postedBeforeFilter);
    },
    [removeFilter, setFilters, setPostedBy],
  );

  const handleReset = useCallback(
    (e: React.MouseEvent<SVGElement>) => {
      e.stopPropagation();
      resetPostedBy();
      removeFilter('published_at');
    },
    [resetPostedBy, removeFilter],
  );

  return {
    selectedTime,
    handleSelectPostedBy,
    handleSelectCustomDates,
    handleReset,
  };
};
